import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import InputText from "components/_refactor/text/input";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import DateField from "components/_refactor/fields/date";
import UploadFile from "components/_refactor/dropzone";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import {
  CAMPAIGN_CATEGORIES,
  CONTENT_STATUS,
  SEGMENTATION_OPTIONS,
  MOCK_CONTENTS,
} from "../constants";

const EMPTY_FORM = {
  nome: "",
  descricao: "",
  categoria: null,
  dataInicio: null,
  dataFim: null,
  status: null,
  destaqueHome: false,
  ordemExibicao: "",
  segmentacao: [],
  tags: "",
  materiaisVinculados: [],
  videosVinculados: [],
};

const materialOptions = MOCK_CONTENTS.filter((c) =>
  ["MATERIAL_PDF", "INSTITUCIONAL", "VM_MERCHANDISING"].includes(c.tipo),
).map((c) => ({ value: c.id, label: c.titulo }));

const videoOptions = MOCK_CONTENTS.filter((c) =>
  ["VIDEO", "INSTITUCIONAL"].includes(c.tipo),
).map((c) => ({ value: c.id, label: c.titulo }));

export default function DialogCampanha({ open, onClose, content }) {
  const snackbar = useSnackbar();
  const [form, setForm] = useState(EMPTY_FORM);
  const [thumbnailFiles, setThumbnailFiles] = useState([]);
  const [bannerFiles, setBannerFiles] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (content) {
      setForm({
        nome: content.titulo || "",
        descricao: content.descricao || "",
        categoria:
          CAMPAIGN_CATEGORIES.find((c) => c.label === content.categoria) ||
          null,
        dataInicio: "",
        dataFim: "",
        status: CONTENT_STATUS.find((s) => s.value === content.status) || null,
        destaqueHome: false,
        ordemExibicao: "1",
        segmentacao: [],
        tags: "",
        materiaisVinculados: [],
        videosVinculados: [],
      });
    } else {
      setForm(EMPTY_FORM);
      setThumbnailFiles([]);
      setBannerFiles([]);
    }
  }, [open, content]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!form.nome?.trim()) {
      snackbar.showMessage("Nome da campanha é obrigatório.", {
        severity: "error",
      });
      return;
    }
    snackbar.showMessage(
      content
        ? "Campanha atualizada com sucesso."
        : "Campanha salva com sucesso.",
      { severity: "success" },
    );
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Typography
          variant="h4"
          component="span"
          style={{ color: themeColorsDesingSystem.text.primary }}
        >
          {content ? "Editar Campanha" : "Cadastro de Campanha"}
        </Typography>
        <IconButton
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Fechar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InputText
                  label="Nome da campanha"
                  value={form.nome}
                  onChange={(e) => updateForm("nome", e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <InputText
                  label="Descrição"
                  value={form.descricao}
                  onChange={(e) => updateForm("descricao", e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Categoria"
                  value={form.categoria}
                  onChange={(v) => updateForm("categoria", v)}
                  options={CAMPAIGN_CATEGORIES}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={6}>
                <DateField
                  label="Data início"
                  value={form.dataInicio}
                  onChange={(v) => updateForm("dataInicio", v)}
                />
              </Grid>
              <Grid item xs={6}>
                <DateField
                  label="Data fim"
                  value={form.dataFim}
                  onChange={(v) => updateForm("dataFim", v)}
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Status"
                  value={form.status}
                  onChange={(v) => updateForm("status", v)}
                  options={CONTENT_STATUS}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.destaqueHome}
                      onChange={(e) =>
                        updateForm("destaqueHome", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Destaque na home"
                  style={{ color: themeColorsDesingSystem.text.primary }}
                />
              </Grid>
              <Grid item xs={12}>
                <InputText
                  label="Ordem de exibição"
                  type="number"
                  value={form.ordemExibicao}
                  onChange={(e) => updateForm("ordemExibicao", e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  Thumbnail
                </Typography>
                <UploadFile
                  message="Arraste a thumbnail ou clique para selecionar"
                  accept={{ "image/*": [".jpg", ".jpeg", ".png"] }}
                  onChangeFiles={setThumbnailFiles}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  Banner principal
                </Typography>
                <UploadFile
                  message="Arraste o banner ou clique para selecionar"
                  accept={{ "image/*": [".jpg", ".jpeg", ".png"] }}
                  onChangeFiles={setBannerFiles}
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Segmentação"
                  value={form.segmentacao}
                  onChange={(v) => updateForm("segmentacao", v ? [v] : [])}
                  options={SEGMENTATION_OPTIONS}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <InputText
                  label="Tags"
                  value={form.tags}
                  onChange={(e) => updateForm("tags", e.target.value)}
                  placeholder="Ex: inverno, coleção, promoção"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Materiais vinculados"
                  value={form.materiaisVinculados[0] || null}
                  onChange={(v) =>
                    updateForm("materiaisVinculados", v ? [v] : [])
                  }
                  options={materialOptions}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Vídeos vinculados"
                  value={form.videosVinculados[0] || null}
                  onChange={(v) => updateForm("videosVinculados", v ? [v] : [])}
                  options={videoOptions}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <Button onClick={onClose} variant="outlined">
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Salvar campanha
        </Button>
      </DialogActions>
    </Dialog>
  );
}
